import { paths } from '@common/types';
import { axiosInstance } from '@config/axios/axios.config';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const editProfileRequest = async (
  props: paths['/profile']['patch']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.patch<
    paths['/profile']['patch']['requestBody']['content']['application/json']
  >(`/profile`, props);
  const data = await response;

  return data;
};

export const useEditProfileMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editProfile'],
    mutationFn: (
      props: paths['/profile']['patch']['requestBody']['content']['application/json'],
    ) => editProfileRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['profile', 'self'] }),
      ]),
  });

  return mutation;
};

const changeProfilePasswordRequest = async (
  props: paths['/profile/change-password']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/profile/change-password']['post']['requestBody']['content']['application/json']
  >(`/profile/change-password`, props);
  const data = await response;

  return data;
};

export const useChangeProfilePasswordMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['changeProfilePassword'],
    mutationFn: (
      props: paths['/profile/change-password']['post']['requestBody']['content']['application/json'],
    ) => changeProfilePasswordRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['profile', 'self'] }),
      ]),
  });

  return mutation;
};

const uploadProfileAvatarRequest = async (props: { file: FormData }) => {
  const response = axiosInstance.put<{
    file: FormData;
  }>(`/profile/avatar`, props.file, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  const data = await response;

  return data;
};

export const useUploadProfileAvatarMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['profileAvatarUpload'],
    mutationFn: (props: { file: FormData }) =>
      uploadProfileAvatarRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['profileAvatar'] }),
        client.invalidateQueries({ queryKey: ['profile', 'self'] }),
      ]),
  });

  return mutation;
};

const deleteProfileAvatarRequest = async () => {
  const response = axiosInstance.delete<{
    id: string;
  }>(`/profile/avatar`);
  const data = await response;

  return data;
};

export const useDeleteProfileAvatarMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['deleteProfileAvatar'],
    mutationFn: () => deleteProfileAvatarRequest(),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['profileAvatar'] }),
        client.invalidateQueries({ queryKey: ['profile', 'self'] }),
      ]),
  });

  return mutation;
};

const profileHeartbeatRequest = async () => {
  const response =
    axiosInstance.post<
      paths['/profile/heartbeat']['post']['responses']['204']['content']
    >(`/profile/heartbeat`);
  const data = await response;

  return data;
};

export const useProfileHeartbeatMutation = () => {
  const mutation = useMutation({
    mutationKey: ['profileHeartbeat'],
    mutationFn: () => profileHeartbeatRequest(),
  });

  return mutation;
};

export const useProfileHeartbeatMutationReactivate = () => {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationKey: ['profileHeartbeat'],
    mutationFn: () => profileHeartbeatRequest(),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['profile', 'self'] }),
      ]),
  });

  return mutation;
};

const deleteMFAMethodRequest = async (
  props: paths['/profile/mfa-method']['delete']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.delete<
    paths['/profile/mfa-method']['delete']['requestBody']['content']['application/json']
  >(`/profile/mfa-method`, { data: { ...props } });
  const data = await response;

  return data;
};

export const useDeleteMFAMethodMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['deleteMFAMethod'],
    mutationFn: (
      props: paths['/profile/mfa-method']['delete']['requestBody']['content']['application/json'],
    ) => deleteMFAMethodRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['profile', 'self'] }),
      ]),
  });

  return mutation;
};

const changeMFAMethodRequest = async (
  props: paths['/profile/default-mfa-method']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/profile/default-mfa-method']['post']['requestBody']['content']['application/json']
  >(`/profile/default-mfa-method`, { ...props });
  const data = await response;

  return data;
};

export const useChangeMFAMethodMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['changeMFAMethod'],
    mutationFn: (
      props: paths['/profile/default-mfa-method']['post']['requestBody']['content']['application/json'],
    ) => changeMFAMethodRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['profile', 'self'] }),
      ]),
  });

  return mutation;
};
